import React, { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Button } from '@mui/material';
import { ButtonGroupWrapper } from '@sharedCR/coreDesignSystem/Buttons';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';

import { HORIZONTAL_ALIGNMENT } from 'constants/ui';

import { closeDialogAction } from 'store/global/actions';
import { getLoanApplicationCommonDataByApplicationIdAction } from 'store/loanApplications/actions';
import { selectLoanAndLockPendingChanges } from 'store/loanApplications/selectors';

import { handleNotAcceptableResponse } from '../../LoanApplication.utils';
import { getProgramsAndPricingTabRequestData } from '../../tabs/propertyAndLoan/EditModal/Content/Content.utils';
import ProgramsAndPricing from '../../tabs/propertyAndLoan/EditModal/Content/MainContent/ProgramsAndPricing';

import { ChooseProgramPopupTestIds } from './ChooseProgramPopup.testIds';

const ChooseProgramPopup = ({
  isFromPropertyAndLoan = true,
  isProgramReselection = false,
  isLoading,
  setIsLoading,
  handlerProps,
  isRevertNeededAfterAccept,
  isSuccessActionNeededAfterAccept,
  onAccept,
  onRevert,
  loanOptionsSource,
}) => {
  const dispatch = useDispatch();

  const { applicationId } = useParams();

  const [entitySideData, setEntitySideData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const pendingChanges = useSelector(selectLoanAndLockPendingChanges);

  const controlsContainer = document.getElementById('choose-program-popup-controls');

  const onCancel = useCallback(() => {
    dispatch(closeDialogAction());
  }, [dispatch]);

  const onSubmit = useCallback(
    e => {
      e.preventDefault();

      setIsSubmitting(true);
      setIsLoading(true);

      const programsAndPricing = getProgramsAndPricingTabRequestData({ entitySideData });

      onAccept({ ...programsAndPricing })
        .then(async () => {
          if (!isSuccessActionNeededAfterAccept) {
            return;
          }

          await dispatch(getLoanApplicationCommonDataByApplicationIdAction({ applicationId }));

          if (isRevertNeededAfterAccept) {
            onRevert();
          }

          dispatch(closeDialogAction());
        })
        .catch(({ response }) => handleNotAcceptableResponse({ ...handlerProps, response }))
        .finally(() => {
          setIsLoading(false);
          setIsSubmitting(false);
        });
    },
    [
      entitySideData,
      setIsLoading,
      dispatch,
      onAccept,
      applicationId,
      isRevertNeededAfterAccept,
      isSuccessActionNeededAfterAccept,
      onRevert,
      handlerProps,
    ],
  );

  return (
    <>
      <ProgramsAndPricing
        isFromPropertyAndLoan={isFromPropertyAndLoan}
        isLoading={isLoading}
        isProgramReselection={isProgramReselection}
        isWithInfoTitle={false}
        loanOptionsSource={loanOptionsSource}
        pendingChanges={pendingChanges}
        setEntitySideData={setEntitySideData}
        setIsLoading={setIsLoading}
      />

      {!!controlsContainer &&
        createPortal(
          <ButtonGroupWrapper
            alignment={HORIZONTAL_ALIGNMENT.RIGHT}
            className="dialog-actions w-full px-4 mt-12"
          >
            <Button
              className="dialog-action"
              data-testid={ChooseProgramPopupTestIds.CANCEL_BUTTON}
              size="large"
              variant="line"
              onClick={onCancel}
            >
              Cancel
            </Button>

            <Button
              className="dialog-action"
              data-testid={ChooseProgramPopupTestIds.CHANGE_PROGRAM_BUTTON}
              disabled={!entitySideData.selectedProgram || isLoading || isSubmitting}
              size="large"
              onClick={onSubmit}
            >
              Change Program
            </Button>
          </ButtonGroupWrapper>,
          controlsContainer,
        )}
    </>
  );
};

ChooseProgramPopup.propTypes = {
  ...WithLoadingPropTypes,
  handlerProps: PropTypes.object.isRequired,
  isRevertNeededAfterAccept: PropTypes.bool.isRequired,
  isSuccessActionNeededAfterAccept: PropTypes.bool.isRequired,
  onAccept: PropTypes.func.isRequired,
  onRevert: PropTypes.func.isRequired,
  isFromPropertyAndLoan: PropTypes.bool,
  isProgramReselection: PropTypes.bool,
  loanOptionsSource: PropTypes.string,
};

export default withLoading(ChooseProgramPopup);
